'use client';

import * as React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Modal from '@mui/material/Modal';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import AppleIcon from '@mui/icons-material/Apple';
import AdbIcon from '@mui/icons-material/Adb';
import WindowIcon from '@mui/icons-material/Window';
import CircularProgress from '@mui/material/CircularProgress';
import {theme} from "@/styles/theme";

const osTypes = [
  { 
      name: 'Windows', 
      icon: <WindowIcon />, 
      downloadUrl: 'https://github.com/Happ-proxy/happ-desktop/releases/latest/download/setup-Happ.x64.exe',
      screenshots: ['/windows-1.jpg']
  },
  { 
      name: 'Apple', 
      icon: <AppleIcon />, 
      downloadUrl: 'https://apps.apple.com/us/app/happ-proxy-utility/id6504287215',
      screenshots: ['/apple-1.jpg', '/apple-2.jpg']
  },
  { 
      name: 'Android', 
      icon: <AdbIcon />, 
      downloadUrl: 'https://play.google.com/store/apps/details?id=com.happproxy',
      screenshots: ['/android-1.jpg', '/android-2.jpg']
  }
];

interface InstallViewProps {
    onBack: () => void;
}

export default function InstallView({ onBack }: InstallViewProps) {
  const [osIndex, setOsIndex] = React.useState(0);
  const [copied, setCopied] = React.useState(false);
  const [modalOpen, setModalOpen] = React.useState(false);
  
  const [vpnKey, setVpnKey] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
      const fetchKey = async () => {
          try {
              setLoading(true);
              const response = await fetch('/vpn/key');
              const data = await response.json();
              
              if (data.success && data.key) {
                  setVpnKey(data.key);
              } else {
                  setError(data.error || 'Не удалось получить ключ');
              }
          } catch (e) {
              setError('Ошибка сети');
          } finally {
              setLoading(false);
          }
      };
      
      fetchKey();
  }, []);

  const handleModalOpen = () => setModalOpen(true);
  const handleModalClose = () => setModalOpen(false);

  const handleOsSelect = (index: number) => {
    setOsIndex(index);
    handleModalClose();
  };

  const handleCopy = () => {
    if (vpnKey) {
        navigator.clipboard.writeText(vpnKey);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    }
  };

  const selectedOS = osTypes[osIndex].name;
  const downloadUrl = osTypes[osIndex].downloadUrl;
  const screenshots = osTypes[osIndex].screenshots;

  const HEADER_HEIGHT = 72;
  const BOTTOM_MARGIN = 16+16;

  const cardStyle = {
      background: "linear-gradient(145deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%)",
      backdropFilter: "blur(10px)",
      p: 2, 
      borderRadius: 1, 
      border: '1px solid rgba(255, 255, 255, 0.12)',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.2), 0 2px 4px -1px rgba(0, 0, 0, 0.1)'
  };

  return (
    <>
      <Box sx={{ 
          position: 'fixed', 
          inset: 0, 
          background: "linear-gradient(rgb(10, 10, 10) 0%, rgb(26, 26, 26) 50%, rgb(10, 10, 10) 100%)",
          zIndex: 9999,
          overflow: 'hidden',
          overscrollBehavior: 'none',
          height: '100dvh',
        }}
      >
        {/* Header */}
        <Box sx={{
          height: `${HEADER_HEIGHT}px`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          bgcolor: 'transparent',
          zIndex: 1001,
          my: 1
        }}>
          <Button 
            onClick={handleModalOpen} 
            endIcon={<ArrowDropDownIcon />}
            sx={{
              fontWeight: 'bold',
              fontSize: '1rem', // Чуть уменьшил шрифт
              color: 'text.primary',
              textTransform: 'none',
              // Стили кнопки выбора ОС
              background: "rgba(255, 255, 255, 0.05)",
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: 50, // Pill shape
              px: 2,
              '&:hover': {
                  background: "rgba(255, 255, 255, 0.1)",
              }
            }}
          >
            {selectedOS}
          </Button>
          <IconButton onClick={onBack} sx={{backgroundColor:theme.palette.background.paper}}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Scrollable Content Container */}
        <Box sx={{ 
            height: `calc(100dvh - ${HEADER_HEIGHT}px - ${BOTTOM_MARGIN}px)`,
            overflowY: 'auto',
            maxWidth: 800, 
            mx: 'auto', 
            width: 'calc(100% - 32px)', 
            
            backgroundImage: "linear-gradient(to bottom right, #262626e6, #171717b3)",
            backdropFilter: "blur(10px)",
            border: '1px solid',
            borderColor: 'divider',
            
            borderRadius: 2,
            p: 3,
            
            '&::-webkit-scrollbar': {
                width: '6px',
            },
            '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(255,255,255,0.2)',
                borderRadius: '3px',
            }
        }}>
            <Typography variant="h6" fontWeight="bold" mb={3}>
                Установка и подключение
            </Typography>

            <Stack spacing={3}>
              {/* Step 1 */}
              <Box sx={cardStyle}>
                <Typography variant="subtitle2" fontWeight="bold" mb={1}>
                  1. Скачайте приложение Happ для {selectedOS} и установите его на ваше устройство
                </Typography>
                <Button 
                    variant="contained" 
                    fullWidth 
                    startIcon={<DownloadIcon />}
                    href={downloadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                  Скачать
                </Button>
              </Box>

              {/* Step 2 */}
              <Box sx={cardStyle}>
                <Typography variant="subtitle2" fontWeight="bold" mb={1}>
                  2. Скопируйте VPN ключ, нажав на него
                </Typography>
                
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : error ? (
                    <Box sx={{ p: 2, border: '1px solid', borderColor: 'error.main', borderRadius: 1, color: 'error.main' }}>
                        <Typography variant="body2" align="center">{error}</Typography>
                    </Box>
                ) : (
                    <Paper 
                    onClick={handleCopy}
                    sx={{ 
                        p: 1.5, 
                        // Еще темнее
                        background: "rgba(0, 0, 0, 0.6)",
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                            borderColor: 'primary.main',
                            background: "rgba(0, 0, 0, 0.7)",
                        }
                    }}
                    >
                    <Typography 
                        variant="body2" 
                        component="code" 
                        sx={{ 
                        color: 'text.secondary',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        mr: 1,
                        fontFamily: 'monospace',
                        }}
                    >
                        {vpnKey}
                    </Typography>
                    <Box sx={{ minWidth: 24, display: 'flex', alignItems: 'center' }}>
                        {copied ? <CheckIcon color="success" fontSize="small" /> : <ContentCopyIcon color="primary" fontSize="small" />}
                    </Box>
                    </Paper>
                )}
              </Box>

              {/* Step 3 */}
              <Box sx={cardStyle}>
                <Typography variant="subtitle2" fontWeight="bold" mb={1}>
                  3. Вставьте ключ в приложение, дождитесь загрузки конфигурации и включите VPN
                </Typography>
                <Stack spacing={2}>
                    {screenshots.map((src, index) => (
                        <Box 
                            key={src}
                            component="img"
                            src={src}
                            alt={`Instruction screenshot ${index + 1}`}
                            sx={{ 
                                width: '100%', 
                                height: 'auto', 
                                borderRadius: 1, 
                                border: '1px solid', 
                                borderColor: 'divider',
                            }} 
                        />
                    ))}
                </Stack>
              </Box>
            </Stack>
        </Box>
      </Box>
      
      {/* OS Selection Modal */}
      <Modal
        open={modalOpen}
        onClose={handleModalClose}
        aria-labelledby="os-selection-modal-title"
        BackdropProps={{
            sx: {
                backdropFilter: 'blur(3px)',
            }
        }}
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 300,
          backgroundImage: "linear-gradient(to bottom right, #262626e6, #171717b3)",
          backdropFilter: "blur(10px)",
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: 24,
          p: 2,
          borderRadius: 1, 
        }}>
          <Typography id="os-selection-modal-title" variant="h6" component="h2" sx={{ mb: 2, textAlign: 'center' }}>
            Выберете устройство
          </Typography>
          <Stack spacing={1}>
            {osTypes.map((os, index) => {
                const isActive = index === osIndex;
                return (
                    <Paper
                        key={os.name}
                        onClick={() => handleOsSelect(index)}
                        elevation={0}
                        sx={{
                            p: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            cursor: 'pointer',
                            background: isActive 
                                ? "rgba(255, 94, 0, 0.15)" 
                                : "linear-gradient(145deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%)",
                            border: '1px solid',
                            borderColor: isActive ? 'primary.main' : 'rgba(255, 255, 255, 0.12)',
                            borderRadius: 1, 
                            width: '100%',
                            transition: 'all 0.2s',
                            '&:hover': {
                                borderColor: 'primary.light',
                                background: isActive 
                                    ? "rgba(255, 94, 0, 0.25)"
                                    : "linear-gradient(145deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.05) 100%)",
                            }
                        }}
                    >
                        <Typography variant="body1" sx={{ fontWeight: 'bold', color: isActive ? 'primary.main' : 'text.primary' }}>{os.name}</Typography>
                        {React.cloneElement(os.icon, { sx: { color: isActive ? 'primary.main' : 'text.secondary' } })}
                    </Paper>
                );
            })}
          </Stack>
        </Box>
      </Modal>
    </>
  );
}
